import React, { PropsWithChildren, useContext, useEffect } from 'react';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import { Agent } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { getLanguageByCode } from '@moderepo/mode-app-components';
import { useLocation } from 'react-router-dom';
import { getPreferredLanguage } from '../../utils/utils';
import * as Constants from '../../utils/constants';
import { i18n } from '../../i18n/i18n';
import translationJson from '../../i18n/translation.json';
import { useGetCurrentProjectId } from '../../utils/customHooks';

/**
 * This is a simple component to wrap the app inside a translation provider.
 * All this component does is listen to the state's currentLanguage and currentProject change and update
 * i18n's current language. When i18n's current language is changed, it should trigger the UI where
 * translation is used to be updated
 */
export const TranslationProvider: React.FC<React.PropsWithChildren<React.ReactNode>> = ({ children }: PropsWithChildren<React.ReactNode>) => {
    const location = useLocation();
    const projectId = useGetCurrentProjectId(location.pathname);
    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const selectedLanguage = getLanguageByCode(
        Constants.SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || Constants.DEFAULT_LANGUAGE;

    useEffect(() => {
        // Remove any override translations incase new project doesn't have any

        Constants.SUPPORTED_LANGUAGES.forEach((language) => {
            i18n.removeResourceBundle(language.code, 'override');
        });
        // Add all language bundles
        if (project && project.supportToolSettings.translations) {
            Constants.SUPPORTED_LANGUAGES.forEach((language) => {
                i18n.addResourceBundle(
                    language.code,
                    'override',
                    project.supportToolSettings.translations?.[language.code]?.override ?? translationJson.translations[language.code].default,
                    true,
                    true,
                );
            });
        }
    }, [project, selectedLanguage]);

    useEffect(() => {
        if (loggedInAgent) {
            // if user is logged in then use the language based on the user preference or project preference
            const preferredLanguage = getPreferredLanguage(project, loggedInAgent, Constants.DEFAULT_LANGUAGE);
            if (i18n.language !== preferredLanguage.code) {
                i18n.changeLanguage(preferredLanguage.code);
            }
        } else if (i18n.language !== selectedLanguage.code) {
            // if user is not logged in, use the language that is currently selected.
            i18n.changeLanguage(selectedLanguage.code);
        }
    }, [loggedInAgent, project, selectedLanguage]);

    return (
        <>
            {children}
        </>
    );
};
